import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, 
  IndianRupee,
  BarChart3,
  RefreshCw,
  Download,
  Store,
  ShoppingCart,
  Target,
  AlertTriangle,
  CheckCircle,
  FileSpreadsheet,
  Users,
  Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SalesIQDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const { languageConfig } = useLanguage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent-dashboards/sales-iq`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load SalesIQ dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/sales-iq/export`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;
    return `₹${value?.toFixed(0) || 0}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!data?.hasData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <TrendingUp className="h-16 w-16 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-700">No Analysis Available</h2>
        <p className="text-slate-500">{data?.message || 'Please run the ReasonSalesIQ agent first.'}</p>
        <Button onClick={() => window.location.href = '/agents'}>Go to Agents</Button>
      </div>
    );
  }

  const overview = data?.revenueOverview || {};
  const storePerf = data?.storePerformance || {};
  const categoryPerf = data?.categoryPerformance || [];
  const riskSignals = data?.riskSignals || {};
  const actionPlan = data?.actionPlan || {};
  const recommendations = data?.recommendations || [];

  const topStores = storePerf?.topStores || [];
  const bottomStores = storePerf?.bottomStores || [];

  const COLORS = ['#22c55e', '#3b82f6', '#f97316', '#ef4444', '#8b5cf6'];

  return (
    <div className="space-y-6" data-testid="sales-iq-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <TrendingUp className="h-8 w-8 text-green-600" />
            <TranslatedText text="SalesIQ Dashboard" cacheKey="salesiq:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Revenue Intelligence & Sales Performance Analysis" cacheKey="salesiq:subtitle" />
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 border-green-200">
        <CardHeader><CardTitle><TranslatedText text="Executive Summary" cacheKey="salesiq:execsummary" /></CardTitle></CardHeader>
        <CardContent>
          <p className="text-slate-700 dark:text-slate-300 whitespace-pre-line" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text={data?.summary} cacheKey="salesiq:summary" />
          </p>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <p className="text-xs text-green-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Total Revenue" cacheKey="salesiq:total_revenue" /></p>
            <p className="text-xl font-bold text-green-900">{formatCurrency(overview.totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <p className="text-xs text-blue-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Transactions" cacheKey="salesiq:transactions" /></p>
            <p className="text-xl font-bold text-blue-900">{overview.totalTransactions?.toLocaleString() || 'N/A'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Units Sold" cacheKey="salesiq:units_sold" /></p>
            <p className="text-xl font-bold text-purple-900">{overview.totalUnits?.toLocaleString() || 'N/A'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-4">
            <p className="text-xs text-orange-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Avg Transaction" cacheKey="salesiq:avg_transaction" /></p>
            <p className="text-xl font-bold text-orange-900">{formatCurrency(overview.avgTransactionValue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
          <CardContent className="pt-4">
            <p className="text-xs text-cyan-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Gross Margin" cacheKey="salesiq:gross_margin" /></p>
            <p className="text-xl font-bold text-cyan-900">{overview.grossMargin || 'N/A'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
          <CardContent className="pt-4">
            <p className="text-xs text-pink-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Target Achievement" cacheKey="salesiq:target_achieve" /></p>
            <p className="text-xl font-bold text-pink-900">{overview.targetAchievement || 'N/A'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Top 5 Recommendations */}
      <Card className="border-green-200 bg-green-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <CheckCircle className="h-5 w-5" />
            <TranslatedText text="Top 5 Revenue Recommendations" cacheKey="salesiq:recommendations_title" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recommendations.slice(0, 5).map((rec, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  idx === 0 ? 'bg-green-500' : idx === 1 ? 'bg-blue-500' : 'bg-slate-500'
                }`}>{idx + 1}</span>
                <div className="flex-1">
                  <p className="font-medium" style={{ fontFamily: languageConfig?.font }}>
                    <TranslatedText text={rec.description || rec.text || rec} cacheKey={`salesiq:rec_${idx}`} />
                  </p>
                  {rec.expectedImpact && <p className="text-sm text-green-600 mt-1">Impact: {rec.expectedImpact}</p>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Store Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-green-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-green-700">Top Performing Stores</CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('top_stores', 'Top Stores')} disabled={exporting === 'top_stores'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {topStores.slice(0, 10).map((store, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-100">
                  <div>
                    <p className="font-medium">{store.storeName || store.storeId}</p>
                    <p className="text-xs text-slate-500">Share: {store.revenueShare} | Margin: {store.margin}</p>
                  </div>
                  <span className="text-sm font-semibold text-green-600">{formatCurrency(store.revenue)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-red-700">Underperforming Stores</CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('bottom_stores', 'Bottom Stores')} disabled={exporting === 'bottom_stores'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {bottomStores.slice(0, 10).map((store, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-100">
                  <div>
                    <p className="font-medium">{store.storeName || store.storeId}</p>
                    <p className="text-xs text-slate-500">{store.issues || `Share: ${store.revenueShare}`}</p>
                  </div>
                  <span className="text-sm font-semibold text-red-600">{formatCurrency(store.revenue)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Signals */}
      <Card className="border-orange-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <AlertTriangle className="h-5 w-5" />
            Risk Signals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-orange-50 rounded-lg">
              <p className="text-xs text-orange-700">Return Rate</p>
              <p className="text-lg font-bold text-orange-900">{riskSignals.returnRate || 'N/A'}</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg">
              <p className="text-xs text-orange-700">Discount Dependency</p>
              <p className="text-lg font-bold text-orange-900">{riskSignals.discountDependency || 'N/A'}</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg">
              <p className="text-xs text-orange-700">Revenue Concentration</p>
              <p className="text-lg font-bold text-orange-900">{riskSignals.revenueConcentration || 'N/A'}</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg">
              <p className="text-xs text-orange-700">Underperforming Stores</p>
              <p className="text-lg font-bold text-orange-900">{riskSignals.underperformingStores || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Performance Chart */}
      {categoryPerf.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Category Performance</CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('category_performance', 'Category Performance')} disabled={exporting === 'category_performance'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryPerf.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                <YAxis tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Export All */}
      <Card className="bg-slate-50">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Export Full Sales Data</h3>
              <p className="text-sm text-slate-500">Download complete sales transactions for analysis</p>
            </div>
            <Button onClick={() => handleExport('all_sales', 'All Sales Data')} disabled={exporting === 'all_sales'}>
              <Download className="h-4 w-4 mr-2" />
              {exporting === 'all_sales' ? 'Exporting...' : 'Export All Sales'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesIQDashboard;
